import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ROUTES } from '../routes';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';
import { useSimClock } from './useSimClock';
import type { HorarioAula, DiaSemana } from '../../types/models';

const DIAS_SEMANA: DiaSemana[] = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];

function getDiaSemanaAtual(refDate?: Date): DiaSemana | null {
  const jsDay = (refDate ?? new Date()).getDay(); // 0=Dom, 1=Seg ... 6=Sab
  if (jsDay === 0) return null; // Domingo — sem aulas, não fazer query
  return DIAS_SEMANA[jsDay - 1];
}

export function getDiaSemanaFromDate(isoDate: string): DiaSemana | null {
  const date = new Date(isoDate + 'T12:00:00');
  const jsDay = date.getDay();
  if (jsDay === 0) return null; // Domingo — sem aulas
  return DIAS_SEMANA[jsDay - 1];
}

/**
 * Hook para Grade de Horários de uma turma.
 * GET /escolas/:escolaId/turmas/:turmaId/horarios
 */
export function useHorarios(turmaId?: string) {
  const escolaId = useAuthStore((s) => s.escolaId);

  const query = useQuery({
    queryKey: ['horarios', escolaId, turmaId],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.horarios.porTurma(escolaId!, turmaId!));
      return (Array.isArray(data) ? data : data.data ?? []) as HorarioAula[];
    },
    enabled: !!escolaId && !!turmaId,
  });

  return {
    horarios: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook para detectar qual aula está acontecendo AGORA para um professor.
 * Usado na Chamada Inteligente (auto-sugestão).
 */
export function useHorarioAtual(professorId?: string, customDate?: string) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const { simDateYmd, isSimulation, virtualEpochMs } = useSimClock();
  const effectiveDate = customDate || (isSimulation ? simDateYmd : undefined);
  const diaSemana = effectiveDate
    ? getDiaSemanaFromDate(effectiveDate)
    : getDiaSemanaAtual();

  const query = useQuery({
    queryKey: [
      'horario-atual',
      escolaId,
      professorId,
      diaSemana,
      effectiveDate,
      simDateYmd,
      // Em sim, reavalia “agora” quando o relógio virtual muda (faixa de hora).
      isSimulation ? Math.floor(virtualEpochMs / 60_000) : 'wall',
    ],
    queryFn: async () => {
      // Se professorId informado, filtra; se não, busca todos da escola
      const params: any = { diaSemana };
      if (professorId) params.professorId = professorId;
      // data = dia civil (sim ou wall) para aplicar cobertura/substituto
      const dataRef = effectiveDate || simDateYmd;
      if (professorId && dataRef) params.data = dataRef;
      const { data } = await api.get(ROUTES.horarios.porEscola(escolaId!), { params });
      const raw: HorarioAula[] = Array.isArray(data) ? data : data.data ?? [];

      // Deduplica intervalos iguais (mesmo horário) que aparecem de turmas diferentes
      const seenIntervalos = new Set<string>();
      const horarios = raw.filter((h) => {
        if (h.tipo !== 'INTERVALO') return true;
        const key = `${h.horaInicio}-${h.horaFim}`;
        if (seenIntervalos.has(key)) return false;
        seenIntervalos.add(key);
        return true;
      });

      if (horarios.length === 0) return { horarioAtual: null, intervaloAtual: null, horariosHoje: horarios };

      // Relógio: virtual na simulação; civil fora dela.
      const agora = isSimulation ? new Date(virtualEpochMs) : new Date();
      const horaAtual = `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;

      const horarioAtual = horarios.find((h) => {
        if (!h.horaInicio || !h.horaFim || h.tipo === 'INTERVALO') return false;
        return horaAtual >= h.horaInicio && horaAtual <= h.horaFim;
      }) ?? null;

      // Detecta se estamos em um intervalo
      const intervaloAtual = horarios.find((h) => {
        if (!h.horaInicio || !h.horaFim || h.tipo !== 'INTERVALO') return false;
        return horaAtual >= h.horaInicio && horaAtual <= h.horaFim;
      }) ?? null;

      return { horarioAtual, intervaloAtual, horariosHoje: horarios };
    },
    enabled: !!escolaId && !!diaSemana,
    refetchInterval: isSimulation ? 5_000 : 60_000,
  });

  return {
    horarioAtual: query.data?.horarioAtual ?? null,
    intervaloAtual: query.data?.intervaloAtual ?? null,
    horariosHoje: query.data?.horariosHoje ?? [],
    isLoading: query.isLoading,
    diaSemana,
    refetch: query.refetch,
  };
}

/**
 * Hook para buscar horarios de um professor em um dia especifico.
 * Usado na Agenda Semanal do Dashboard Professor.
 */
export function useHorariosPorDia(professorId?: string, dia?: DiaSemana) {
  const escolaId = useAuthStore((s) => s.escolaId);

  const query = useQuery({
    queryKey: ['horarios-dia', escolaId, professorId, dia],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.horarios.porEscola(escolaId!), {
        params: { professorId, diaSemana: dia },
      });
      const horarios: HorarioAula[] = Array.isArray(data) ? data : data.data ?? [];
      return horarios.sort((a, b) => (a.horaInicio || '').localeCompare(b.horaInicio || ''));
    },
    enabled: !!escolaId && !!professorId && !!dia,
  });

  const raw = query.data;
  const aulas: HorarioAula[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { aulas?: HorarioAula[] }).aulas)
      ? (raw as { aulas: HorarioAula[] }).aulas
      : [];

  return {
    aulas,
    isLoading: query.isLoading,
  };
}

/**
 * Hook para gerar grade e salvar em bulk.
 */
export function useHorariosMutation(turmaId?: string) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const queryClient = useQueryClient();
  const basePath = ROUTES.horarios.porTurma(escolaId!, turmaId!);

  const gerar = useMutation({
    mutationFn: (config: { aulasPorDia: number; incluirSabado: boolean }) =>
      api.post(`${basePath}/gerar`, config),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['horarios', escolaId, turmaId] }),
  });

  const salvarBulk = useMutation({
    mutationFn: (horarios: Partial<HorarioAula>[]) =>
      api.put(`${basePath}/bulk`, { horarios }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['horarios', escolaId, turmaId] }),
  });

  return { gerar, salvarBulk };
}
